import { AccountConfirmationRequest, AccountResetPasswordRequest, ChangeAccountTypeRequest, EmailResendRequest, EnterpriseAccountConfirmationTokenRequest, IAccountUpdatePasswordRequest, IAuthenticationRequest, ICreateAccountRequest, IUserLoginInfo, ResetPasswordRequest, SSOProvider } from "../entities/account";
import { DataService } from "./data-service";
import { ITooltipUserGuideLocalState } from "../entities/tooltip-user-guide";

class AccountService extends DataService {
  constructor() {
    super("account", 2);
  }

  login(accountRequest: IAuthenticationRequest) {
    return this.api.post<IUserLoginInfo>("session", accountRequest);
  }

  logout() {
    return this.api.post<{ msg: string }>("logout");
  }

  getCurrentUser() {
    return this.api.get<IUserLoginInfo>("get-current");
  }

  signUp(accountRequest: ICreateAccountRequest) {
    return this.api.post<{ msg: string }>("sign-up", accountRequest);
  }

  requestResetPassword(resetPasswordRequest: ResetPasswordRequest) {
    return this.api.post<{ msg: string }>("request-reset-password", resetPasswordRequest);
  }

  resetPassword(accountPasswordUpdateRequest: AccountResetPasswordRequest) {
    return this.api.post<{ msg: string }>("reset-password", accountPasswordUpdateRequest);
  }

  confirmAccount(accountConfirmationRequest: AccountConfirmationRequest) {
    return this.api.post<{ msg: string }>("confirmation", accountConfirmationRequest);
  }

  confirmEnterpriseAccount(request: EnterpriseAccountConfirmationTokenRequest) {
    return this.api.post<{ msg: string }>("confirmation-enterprise", request);
  }

  resendEmailResetPassword(request: EmailResendRequest) {
    return this.api.post<{ msg: string }>("email-resend/reset-password", request);
  }

  resendEmailAccountConfirmation(request: EmailResendRequest) {
    return this.api.post<{ msg: string }>("email-resend/confirmation", request);
  }

  changeAccountType(request: ChangeAccountTypeRequest) {
    return this.api.post<{ msg: string }>("change-account-type", request);
  }

  validateOrganization(name: string) {
    return this.api.post<{ msg: string }>("validate-organization", { name });
  }

  setTooltipUserGuideState(showTooltip: boolean) {
    let tooltipGuideState: ITooltipUserGuideLocalState  = {
      showTooltip
    };

    localStorage.setItem(tooltipUserGuideSessionStoragePropName, JSON.stringify(tooltipGuideState));
  }

  getTooltipUserGuideState(): ITooltipUserGuideLocalState {
    let tooltipGuideState = localStorage.getItem('tooltip-user-guide');
    const defaultTooltipGuideState: ITooltipUserGuideLocalState = {
      showTooltip: true
    };

    if (!tooltipGuideState) {
      this.setTooltipUserGuideState(true);
      return defaultTooltipGuideState;
    }

    return JSON.parse(tooltipGuideState) as ITooltipUserGuideLocalState;
  }

  setPassword(accountPasswordUpdateRequest: Omit<IAccountUpdatePasswordRequest, "currentPassword">) {
    return this.api.post<{ msg: string }>("set-password", accountPasswordUpdateRequest);
  }

  updatePassword(accountPasswordUpdateRequest: IAccountUpdatePasswordRequest) {
    return this.api.post<{ msg: string }>("update-password", accountPasswordUpdateRequest);
  }

  getSSOLoginUrl(ssoProvider: SSOProvider, redirectUri: string) {
    let url = ssoUrlDict[ssoProvider];
    redirectUri = btoa(redirectUri);
    return this.api.get<{url: string}>(url, { redirectUri }, null);
  }

  detachExternalAccount() {
    return this.api.get<{ msg: string }>("detach-external-account");
  }
}

export const accountService = new AccountService();

const tooltipUserGuideSessionStoragePropName = 'tooltip-user-guide';

const ssoUrlDict = {
  [SSOProvider.Microsoft]: "azure/login"
};
